/**
 * Map raw backend records to the `OrderListItem` shape the table renders.
 *
 * Field mapping is taken from Vue's worker (`order-worker.ts:200-360`):
 *
 *  | Table column   | Backend field(s)                                       |
 *  | -------------- | ------------------------------------------------------ |
 *  | Order #        | `ecomOrderNumber` (fallback: `refId`)                  |
 *  | Date           | `processedAt` (fallback: `createdAt`)                  |
 *  | Updated (sub)  | `lastUpdatedAt`                                        |
 *  | Customer       | `customerFirstName + ' ' + customerLastName`           |
 *  | Email (sub)    | `email` (fallback: `customerEmail`)                    |
 *  | Total          | `grandTotal` (number) → formatted currency             |
 *  | PO Number      | `poNumber`                                             |
 *  | Items          | `lineItems.length`                                     |
 *  | Payment        | `orderFinancialStatus` (PAID / PENDING / ...)          |
 *  | Method (slide) | `paymentMethod`                                        |
 *  | Shipping       | `shippingMethod`                                       |
 *  | Shipment (sub) | `maxShipmentDate`                                      |
 *  | Sales Channel  | `distributor` ?? `distributorGroup` ?? `customerType`  |
 *  |                | ?? 'N/A'                                               |
 *
 * The sales-channel chain was verified against a live
 * `/v1/orders/order/{refId}` response — see `mapRawOrderToListItem`.
 */
import {
  SHOPIFY_ENTITY_TYPE,
  constructShopifyUrl,
} from 'utils/constructShopifyUrl';
import { formatMoney } from 'utils/numberUtils';

import { joinName } from './joinName';
import {
  ORDERS_DATE_LOCALE,
  ORDERS_NA_SENTINEL as NA,
  ORDERS_TIMEZONE_LABEL,
} from '../constants';
import type { OrderListItem, OrderPaymentStatus } from '../types/order';
import type { OrdersSearchResponse, RawOrderRecord } from '../types/payload';


/**
 * NO `timeZone` option here — and none may be added. The backend sends
 * `processedAt` / `lastUpdatedAt` as `YYYY-MM-DD HH:mm:ss` strings
 * ALREADY in Central time, and Vue reformats that wall clock verbatim
 * (`worker.ts` → `getFormatedDateAndTime`: moment parse → format, zero
 * timezone math). Parsing the string as local time and formatting it
 * back in local time is the same identity on the wall-clock fields.
 * This formatter previously formatted with `timeZone:
 * 'America/Chicago'`, which re-interpreted the Central wall clock in
 * the BROWSER's zone and converted it to Chicago — shifting every date
 * by the viewer's offset from Central (+10h30m for an IST viewer).
 */
const DATE_FMT = new Intl.DateTimeFormat(ORDERS_DATE_LOCALE, {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hour12: true,
});

/**
 * Wall-clock `Date` → "MM/DD/YYYY HH:MM AM" (no zone label). Intl emits
 * "MM/DD/YYYY, HH:MM AM"; the comma is stripped to match the prototype
 * verbatim. Shared with the Timeline tab so the two never drift.
 */
export function formatOrderDateTime(d: Date): string {
  return DATE_FMT.format(d).replace(',', '');
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return NA;
  const d = new Date(iso.includes('T') ? iso : iso.replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return NA;
  return `${formatOrderDateTime(d)} ${ORDERS_TIMEZONE_LABEL}`;
}


/**
 * Normalise `orderFinancialStatus` to the two badge states the table
 * supports. Anything we don't recognise renders as "Pending" rather
 * than crashing — the actual raw value can be inspected via DevTools.
 */
function normalisePaymentStatus(raw: unknown): OrderPaymentStatus {
  if (typeof raw !== 'string') return 'Pending';
  const lower = raw.trim().toLowerCase();
  if (
    lower === 'paid'
    || lower === 'complete'
    || lower === 'completed'
    || lower === 'fully_paid'
  ) {
    return 'Paid';
  }
  return 'Pending';
}

function pickFirstNonEmpty(...candidates: ReadonlyArray<unknown>): string | null {
  for (const c of candidates) {
    if (typeof c === 'string' && c.trim() !== '') return c;
  }
  return null;
}

/**
 * Map a single raw record. Exported separately so tests / Storybook can
 * drive it without instantiating the whole response envelope.
 */
export function mapRawOrderToListItem(raw: RawOrderRecord): OrderListItem {
  // Sales-channel resolution — verified against a live
  // `/v1/orders/order/{refId}` response. The MEDIUM search payload
  // ships `distributor` (e.g. "GFS Canada", "Amazon DF") as the
  // display label, with `distributorGroup` ("GFS", "Amazon") as the
  // parent group and `customerType` as a near-duplicate fallback.
  //
  // Fallback order:
  //   1. `distributor`      — primary label the column should show
  //   2. `distributorGroup` — group label when the leaf is missing
  //   3. `customerType`     — last-resort tag (matches what the
  //                          detail-channel diagnostic surfaced)
  //
  // The earlier `distributorName` / `salesChannel` / `channel`
  // candidates didn't appear on any observed record; removing them
  // keeps the fallback chain honest about what the wire ships.
  const channel = pickFirstNonEmpty(
    raw.distributor,
    raw.distributorGroup,
    raw.customerType,
  );

  return {
    refId: raw.refId,
    id: raw.ecomOrderNumber ?? raw.refId,
    date: formatDate(raw.processedAt ?? raw.createdAt),
    upd: formatDate(raw.lastUpdatedAt),
    status: raw.orderFulfillmentStatus ?? '',
    cust: joinName(raw.customerFirstName, raw.customerLastName) ?? NA,
    email: raw.email ?? raw.customerEmail ?? NA,
    total: formatMoney(raw.grandTotal, raw.currency, NA),
    grandTotal:
      typeof raw.grandTotal === 'number' && !Number.isNaN(raw.grandTotal)
        ? raw.grandTotal
        : undefined,
    po: raw.poNumber && raw.poNumber.trim() !== '' ? raw.poNumber : NA,
    items: raw.lineItems?.length ?? 0,
    pay: normalisePaymentStatus(raw.orderFinancialStatus),
    method: raw.paymentMethod ?? NA,
    ship: raw.shippingMethod ?? NA,
    shipDate: formatDate(raw.maxShipmentDate),
    chan: channel ?? NA,
    storeRefId: raw.storeRefId ?? undefined,
    // Vue parity (worker.ts `redirectUrl`): Shopify admin deep-link for
    // the Order # cell. Vue builds it unconditionally; we require both
    // ids so a row without them renders plain text instead of a link
    // to a junk URL.
    redirectUrl:
      raw.storeRefId && raw.ecomEntityId
        ? constructShopifyUrl(
            raw.storeRefId,
            raw.ecomEntityId,
            SHOPIFY_ENTITY_TYPE.ORDERS,
          )
        : undefined,
  };
}

export interface MappedOrdersResult {
  rows: OrderListItem[];
  totalCount: number;
}

export function mapOrdersSearchResponse(
  response: OrdersSearchResponse | undefined,
): MappedOrdersResult {
  if (!response?.data) {
    return { rows: [], totalCount: response?.stats?.count ?? 0 };
  }
  // Backend returns an object keyed by refId — Vue's worker iterates the
  // keys. We preserve iteration order so the visible sort stays whatever
  // the backend resolved.
  const rawRows = Object.values(response.data);
  const rows = rawRows.map(mapRawOrderToListItem);
  const totalCount = response.stats?.count ?? rows.length;
  return { rows, totalCount };
}
